import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { GrupoMember } from "./GrupoMemberListSection";

interface UnlinkGrupoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: GrupoMember | null;
  controladoraId: number;
  onSuccess?: () => void;
}

/**
 * UnlinkGrupoModal
 * Modal para remover empresa do grupo econômico
 * 
 * Campos:
 * - Motivo obrigatório (textarea)
 * - Aviso se empresa é Matriz (filiais desvinculadas em cascata)
 * 
 * Validações:
 * - Motivo não pode estar vazio
 * - Backend valida regras críticas
 */
export default function UnlinkGrupoModal({
  open,
  onOpenChange,
  member,
  controladoraId,
  onSuccess,
}: UnlinkGrupoModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Mutation: Desvinc ular empresa do grupo
  const unlinkMutation = trpc.company.group.unlinkGrupo.useMutation({
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      setError(error.message || "Erro ao remover empresa do grupo");
    },
  });

  const handleReasonChange = useCallback((value: string) => {
    setReason(value);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!reason.trim()) {
      setError("Motivo é obrigatório");
      return;
    }

    if (reason.trim().length < 10) {
      setError("Motivo deve ter no mínimo 10 caracteres");
      return;
    }

    if (!member) {
      setError("Empresa não selecionada");
      return;
    }

    try {
      await unlinkMutation.mutateAsync({
        controladoraId,
        companyId: member.id,
        motivo: reason.trim(),
      });
    } catch (err: any) {
      setError(err.message || "Erro ao remover empresa do grupo");
    }
  }, [reason, member, unlinkMutation]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setReason("");
    setError(null);
  }, [onOpenChange]);

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remover do Grupo</DialogTitle>
          <DialogDescription>
            Você está prestes a remover <strong>{member.name}</strong> do grupo econômico.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Aviso se Matriz */}
          {member.isMatriz && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Atenção:</strong> Esta empresa é uma Matriz. Suas filiais serão desvinculadas em cascata.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">Motivo da Remoção *</Label>
            <Textarea
              id="reason"
              placeholder="Explique o motivo da remoção desta empresa do grupo..."
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              disabled={unlinkMutation.isPending}
              className="min-h-24"
            />
            <p className={`text-xs ${
              reason.length < 10
                ? "text-destructive font-medium"
                : "text-muted-foreground"
            }`}>
              {reason.length}/10 caracteres mínimo
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleSubmit}
              variant="destructive"
              className="flex-1"
              disabled={!reason.trim() || unlinkMutation.isPending}
            >
              {unlinkMutation.isPending ? ( // isLoading
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removendo...
                </>
              ) : (
                "Remover do Grupo"
              )}
            </Button>
            <DialogClose asChild>
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Cancelar
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
